'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const teamMembers = [
  { name: 'Dr. James Carter', role: 'CEO & Co-Founder', bio: 'Former NSA cybersecurity analyst with 20+ years in offensive security. PhD in Computer Science from MIT.', icon: '👨‍💻' },
  { name: 'Sarah Mitchell', role: 'CTO & Co-Founder', bio: 'Ex-Google security engineer. Led security for Fortune 500 companies. OSCP, CISSP certified.', icon: '👩‍💻' },
  { name: 'Raj Sharma', role: 'VP of Security Research', bio: 'Discovered 50+ CVEs in major software. Former lead researcher at a top-tier security firm.', icon: '👨‍🔬' },
  { name: 'Emily Chen', role: 'Head of Compliance', bio: 'Certified ISO 27001 Lead Auditor, CISA, and CISM. 15 years helping organizations achieve compliance.', icon: '👩‍💼' },
  { name: 'Marcus Johnson', role: 'Director of Red Team', bio: 'Former military cyber operations specialist. Expert in advanced persistent threat simulation.', icon: '👨‍💼' },
  { name: 'Priya Patel', role: 'Head of AI Security', bio: 'Pioneer in AI/ML security research. Published researcher on adversarial machine learning attacks.', icon: '👩‍🔬' },
];

const milestones = [
  { year: '2009', title: 'Founded', desc: 'TechLabs founded by cybersecurity veterans with a mission to make enterprise security accessible.' },
  { year: '2012', title: 'ISO 27001 Certified', desc: 'Achieved ISO 27001 certification, establishing our commitment to information security excellence.' },
  { year: '2015', title: '100th Client', desc: 'Reached 100 enterprise clients across BFSI, healthcare, and technology sectors.' },
  { year: '2018', title: 'AI Platform Launch', desc: 'Launched our proprietary AI-driven security platform, revolutionizing vulnerability management.' },
  { year: '2020', title: 'SOC 2 Attested', desc: 'Achieved SOC 2 Type II attestation, demonstrating our security and availability controls.' },
  { year: '2022', title: 'Global Expansion', desc: 'Expanded operations to 15 countries with 200+ certified security professionals.' },
  { year: '2024', title: '500+ Clients', desc: 'Secured over 500 organizations worldwide with our integrated security and compliance platform.' },
];

export default function AboutPage() {
  return (
    <div style={{ fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }}>
      <Navbar />
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #07182a 0%, #0a1d2f 50%, #0d2640 100%)',
        paddingTop: '140px',
        paddingBottom: '80px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(34,197,94,0.07) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div style={{ maxWidth: '90%', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '100px', padding: '6px 16px', marginBottom: '20px' }}>
            <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: 500 }}>Founded 2009 | 15+ Years of Excellence</span>
          </div>
          <h1 style={{ fontSize: '48px', fontWeight: 700, color: '#fff', lineHeight: 1.2, margin: '0 0 20px' }}>
            Securing the Digital World,{' '}
            <span style={{ color: '#22c55e' }}>One Organization at a Time</span>
          </h1>
          <p style={{ fontSize: '18px', color: '#9ca3af', maxWidth: '700px', margin: '0 auto 32px', lineHeight: '1.7' }}>
            TechLabs is a team of passionate cybersecurity professionals dedicated to making the digital world safer. We combine cutting-edge AI with deep human expertise to deliver security that actually works.
          </p>
        </div>
      </section>
      {/* Mission & Vision */}
      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', marginBottom: '60px' }}>
            <div style={{ padding: '40px', background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', borderRadius: '16px', border: '1px solid rgba(34,197,94,0.2)' }}>
              <div style={{ fontSize: '36px', marginBottom: '16px' }}>🎯</div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#22c55e', margin: '0 0 16px' }}>Our Mission</h2>
              <p style={{ fontSize: '15px', color: '#9ca3af', lineHeight: '1.7', margin: 0 }}>
                To empower organizations with the intelligence, tools, and expertise they need to stay ahead of cyber threats. We believe every organization deserves enterprise-grade security, regardless of size or budget.
              </p>
            </div>
            <div style={{ padding: '40px', background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', borderRadius: '16px', border: '1px solid rgba(34,197,94,0.2)' }}>
              <div style={{ fontSize: '36px', marginBottom: '16px' }}>🔭</div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#22c55e', margin: '0 0 16px' }}>Our Vision</h2>
              <p style={{ fontSize: '15px', color: '#9ca3af', lineHeight: '1.7', margin: 0 }}>
                A world where organizations can innovate freely, knowing their digital assets are protected by intelligent, continuous security. We envision a future where cyber attacks are detected and neutralized before they cause harm.
              </p>
            </div>
            <div style={{ padding: '40px', background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', borderRadius: '16px', border: '1px solid rgba(34,197,94,0.2)' }}>
              <div style={{ fontSize: '36px', marginBottom: '16px' }}>💪</div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#22c55e', margin: '0 0 16px' }}>Our Values</h2>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {['Integrity in everything we do', 'Excellence in security research', 'Transparency with our clients', 'Innovation-driven approach', 'Continuous learning & growth']?.map((v) => (
                  <li key={v} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#9ca3af' }}>
                    <span style={{ color: '#22c55e' }}>✓</span> {v}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px' }}>
            {[
              { value: '500+', label: 'Organizations Secured' },
              { value: '200+', label: 'Certified Experts' },
              { value: '15+', label: 'Years of Experience' },
              { value: '99%', label: 'Client Satisfaction' },
              { value: '50K+', label: 'Vulnerabilities Found' },
              { value: '20+', label: 'Countries Served' },
            ]?.map((stat) => (
              <div key={stat?.label} style={{ textAlign: 'center', padding: '28px 20px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '32px', fontWeight: 700, background: 'linear-gradient(90deg, #16a34a, #22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '8px' }}>{stat?.value}</div>
                <div style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Timeline */}
      <section style={{ background: '#f9fafb', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
              Our <span style={{ color: '#22c55e' }}>Journey</span>
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '500px', margin: '0 auto' }}>15+ years of protecting organizations from cyber threats.</p>
          </div>
          <div style={{ position: 'relative' }}>
            {/* Timeline line */}
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', background: 'linear-gradient(to bottom, #22c55e, #16a34a)', transform: 'translateX(-50%)' }} className="hidden md:block" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {milestones?.map((m, i) => (
                <div key={m?.year} style={{ display: 'flex', gap: '24px', alignItems: 'center', flexDirection: i % 2 === 0 ? 'row' : 'row-reverse' }}>
                  <div style={{ flex: 1, textAlign: i % 2 === 0 ? 'right' : 'left' }}>
                    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px 24px', display: 'inline-block', maxWidth: '400px', textAlign: 'left' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#22c55e', marginBottom: '6px' }}>{m?.year}</div>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 8px' }}>{m?.title}</h3>
                      <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{m?.desc}</p>
                    </div>
                  </div>
                  <div style={{ width: '40px', height: '40px', background: '#22c55e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, boxShadow: '0 0 0 4px rgba(34,197,94,0.2)' }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                  <div style={{ flex: 1 }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Team */}
      <section style={{ background: '#fff', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
              Meet Our <span style={{ color: '#22c55e' }}>Leadership Team</span>
            </h2>
            <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
              World-class security professionals with decades of combined experience protecting organizations from cyber threats.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' }}>
            {teamMembers?.map((member) => (
              <div key={member?.name} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '28px', textAlign: 'center', transition: 'transform 0.2s, box-shadow 0.2s' }} className="hover:-translate-y-1 hover:shadow-lg">
                <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', margin: '0 auto 16px', border: '3px solid rgba(34,197,94,0.3)' }}>
                  {member?.icon}
                </div>
                <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 4px' }}>{member?.name}</h3>
                <p style={{ fontSize: '13px', color: '#22c55e', fontWeight: 600, margin: '0 0 12px' }}>{member?.role}</p>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{member?.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Certifications */}
      <section style={{ background: '#f9fafb', padding: '80px 0' }}>
        <div style={{ maxWidth: '90%', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 16px' }}>
            Credentials That Define <span style={{ color: '#22c55e' }}>Our Expertise</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '600px', margin: '0 auto 48px' }}>
            Our team holds the industry&apos;s most respected certifications, ensuring you receive world-class security expertise.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
            {['OSCP', 'CEH', 'CISA', 'CISM', 'CISSP', 'CREST', 'eWPTX', 'ISO 27001 LA', 'CRTA', 'GPEN', 'GWAPT', 'CompTIA Security+']?.map((cert) => (
              <div key={cert} style={{ background: 'linear-gradient(135deg, #0a1d2f, #1a3a50)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, color: '#22c55e' }}>
                {cert}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section style={{ background: '#0b0e14', padding: '80px 0' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center', padding: '0 5%' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#fff', margin: '0 0 16px' }}>Join 500+ Organizations That Trust TechLabs</h2>
          <p style={{ fontSize: '16px', color: '#9ca3af', margin: '0 0 32px', lineHeight: '1.6' }}>
            Let our certified experts assess your security posture and help you build a stronger defense.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ padding: '14px 36px', background: '#22c55e', borderRadius: '100px', color: '#fff', textDecoration: 'none', fontSize: '17px', fontWeight: 600 }} className="hover:bg-green-600">
              Book a Demo
            </Link>
            <Link href="/services" style={{ padding: '14px 36px', background: 'transparent', borderRadius: '100px', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', textDecoration: 'none', fontSize: '17px', fontWeight: 400 }} className="hover:border-green-400 hover:text-green-400">
              Explore Services
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
